import { useCallback } from 'react';

import { useSetAtom } from 'jotai';

import { getErroredFormsAtomsAtom } from '../../atoms';
import { useFormyContext } from '../useFormyContext';
import { ScrollParams, ScrollToErrorParams } from './scrolling.types';
import { useFormScroll } from './useFormScroll';

/**
 * Gets the element closest to the top of the parent element
 * @param elements - The elements to sort
 * @param parentElement - The parent element to calculate the offset from
 * @returns The elements sorted by the offset from the parent element
 */
const getElementClosestToTop = (elements: HTMLElement[], parentElement?: HTMLElement) => {
  const parentRect = parentElement?.getBoundingClientRect();
  return [...elements].sort((elementA, elementB) => {
    const aRect = elementA.getBoundingClientRect();
    const bRect = elementB.getBoundingClientRect();
    const aOffset = aRect.top - (parentRect?.top ?? 0);
    const bOffset = bRect.top - (parentRect?.top ?? 0);
    return aOffset - bOffset;
  });
};

/**
 * Gets the element closest to the bottom of the parent element
 * @param elements - The elements to sort
 * @param parentElement - The parent element to calculate the offset from
 * @returns The elements sorted by the offset from the parent element
 */
const getElementClosestToBottom = (elements: HTMLElement[], parentElement?: HTMLElement) => {
  const parentRect = parentElement?.getBoundingClientRect();
  return [...elements].sort((elementA, elementB) => {
    const aRect = elementA.getBoundingClientRect();
    const bRect = elementB.getBoundingClientRect();
    const aOffset = aRect.bottom - (parentRect?.top ?? 0);
    const bOffset = bRect.bottom - (parentRect?.top ?? 0);
    return bOffset - aOffset;
  });
};

/**
 * Enables scrolling to an error in the form
 * can be used to target either forms with errors or errored inputs within a form
 */
const useScrollToError = ({ scrollParams }: { scrollParams?: ScrollParams }) => {
  const { scrollToNode } = useFormScroll(scrollParams);
  const { store, formScrollableRef } = useFormyContext();
  const getErroredForms = useSetAtom(getErroredFormsAtomsAtom, { store });

  /** Gets all nodes from any forms with errors */
  const getAllErroredInputs = useCallback(() => {
    const erroredForms = getErroredForms();
    const erroredInputs = erroredForms.map((form) => {
      const errors = form.errors ?? {};
      const inputs = Object.keys(errors).map((key) => {
        return form.getInputNode?.(key);
      });
      return inputs.flat();
    });
    return erroredInputs.flat();
  }, [getErroredForms]);

  /**
   * Scrolls to an error in the form - can choose to scroll to the first error or the last error determined by the location of the error in relation to the form provider
   * by either the one closest to the top or the one closest to the bottom of the form provider.
   *
   * Allows choosing between to the form wrapper or the input wrapper
   */
  const scrollToError = useCallback(
    ({ scrollTo = 'form', startingFrom = 'top', ...args }: ScrollToErrorParams = {}) => {
      const erroredForms = getErroredForms();

      /** List of valid html elements that have errors */
      const erroredElements =
        scrollTo === 'input'
          ? (getAllErroredInputs().filter(Boolean) as HTMLElement[])
          : (erroredForms.map((form) => form.formRef?.current).filter(Boolean) as HTMLElement[]);

      // If there are no errored elements, return as we can't scroll to them
      if (!erroredElements.length) return;

      const erroredElementsInOrder =
        startingFrom === 'top'
          ? getElementClosestToTop(erroredElements, formScrollableRef?.current ?? undefined)
          : getElementClosestToBottom(erroredElements, formScrollableRef?.current ?? undefined);

      const firstErroredElement = erroredElementsInOrder[0];
      if (firstErroredElement) {
        scrollToNode(firstErroredElement, args);
      }
    },
    [formScrollableRef, getAllErroredInputs, getErroredForms, scrollToNode]
  );

  return { scrollToError };
};

export { useScrollToError };
